/* eslint-disable indent */ import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  styled,
  alpha,
  Popover,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageRedactor from "../image-redactor/ImageRedactor";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import { Modal } from "./components/Modal";
import { DOCUMENT_REQUIREMENTS } from "./types/documentRequirements";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

type Platform = "AGENT" | "LANDLORD" | "TENANT" | string;

interface DocumentUploadSectionProps {
  documents: any[];
  onFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    redactedImage?: string
  ) => void;
  onViewDocument: (document: any) => void;
  employmentType: string;
  fullScreen?: boolean;
  platform: Platform;
}
const DocCard = styled(Box)(({ theme }) => ({
  minHeight: 145,
  borderRadius: 16,
  backdropFilter: "blur(12px)",
  background:
    theme.palette.mode === "light"
      ? "rgba(255,255,255,0.65)"
      : "rgba(30,30,30,0.45)",
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  transition: "transform .25s ease, box-shadow .25s ease",
}));

const DocumentUploadSection: React.FC<DocumentUploadSectionProps> = ({
  documents,
  onFileChange,
  onViewDocument,
  employmentType,
  fullScreen = false,
  platform,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  const [currentInfo, setCurrentInfo] = useState<string>("");
  const [redactionModalOpen, setRedactionModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const config =
    DOCUMENT_REQUIREMENTS[
      employmentType as keyof typeof DOCUMENT_REQUIREMENTS
    ] || DOCUMENT_REQUIREMENTS.EMPLOYEE;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    docType: string
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    const allowedTypes =
      docType === "IDENTITY_FRONT" || docType === "IDENTITY_BACK"
        ? imageTypes
        : [...imageTypes, "application/pdf"];

    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message:
          docType === "IDENTITY_FRONT" || docType === "IDENTITY_BACK"
            ? t("documentUploadPanel.id.message")
            : t("documentUploadPanel.documents.message"),
        severity: "error",
      });
      return;
    }

    if (docType === "IDENTITY_FRONT" || docType === "IDENTITY_BACK") {
      setPreviewUrl(URL.createObjectURL(file));
      setSelectedFile(file);
      setSelectedDocType(docType);
      setRedactionModalOpen(true);
      return;
    }

    onFileChange(e, docType);
  };

  const handleRedactedSave = (dataUrl: string) => {
    if (!selectedFile) return;
    fetch(dataUrl)
      .then((r) => r.blob())
      .then((blob) => {
        const redacted = new File([blob], selectedFile.name, {
          type: selectedFile.type,
        });
        const synthetic = {
          target: { files: [redacted] },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onFileChange(synthetic, selectedDocType);
      })
      .finally(() => {
        setRedactionModalOpen(false);
        setPreviewUrl("");
        setSelectedFile(null);
      });
  };
  const openInfo = Boolean(infoAnchor);
  const renderCard = (
    docType: string,
    cfg: (typeof config)[keyof typeof config]
  ) => {
    const uploaded = Boolean(
      documents.find((d) => d.screeningDocumentType === docType)
    );
    return (
      <Grid key={docType} item xs={12} sm={fullScreen ? 3 : 6}>
        <DocCard
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: 1.6,
          }}
        >
          <Box>
            <Box
              display="flex"
              justifyContent={"space-between"}
              alignItems="center"
              gap={1}
            >
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  onMouseLeave={() => setInfoAnchor(null)}
                >
                  {t(cfg.label)}
                </Typography>
                {cfg.required && (
                  <Typography
                    sx={{ opacity: 0.8, color: "#cd0000" }}
                    fontStyle={"italic"}
                    fontSize={12}
                  >
                    {`* ${t("screening.documents.required")}`}
                  </Typography>
                )}
              </Box>
              {uploaded && <CheckCircleIcon color="primary" fontSize="small" />}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35 }}
            >
              {t(cfg.info)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            {platform !== "LANDLORD" && (
              <Button
                component="label"
                sx={{
                  borderRadius: 4,
                  background: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  "&:hover": { background: theme.palette.primary.light },
                }}
                startIcon={<CloudUploadIcon />}
                disableElevation
                variant="contained"
                size="small"
              >
                <input
                  type="file"
                  hidden
                  accept={
                    docType === "IDENTITY_FRONT" || docType === "IDENTITY_BACK"
                      ? "image/*"
                      : "image/*,.pdf"
                  }
                  onClick={(e: any) => (e.target.value = null)}
                  onChange={(e) => handleFileSelect(e, docType)}
                />
                {uploaded
                  ? t("screening.documents.reupload")
                  : t("screening.documents.upload")}
              </Button>
            )}
            {uploaded ? (
              <Button
                onClick={() =>
                  onViewDocument(
                    documents.find((d) => d.screeningDocumentType === docType)!
                  )
                }
                component="label"
                sx={{
                  borderRadius: 4,
                  background: theme.palette.secondary.light,
                  color: theme.palette.secondary.main,
                  "&:hover": { background: theme.palette.secondary.light },
                }}
                startIcon={<VisibilityIcon fontSize="small" />}
                disableElevation
                variant="contained"
                size="small"
              >
                {t("screening.documents.view")}
              </Button>
            ) : (
              <Chip
                label={t("screening.documents.missing")}
                icon={<CloudOffIcon sx={{ color: "#cd0000 !important" }} />}
                sx={{
                  backgroundColor: "#FDD",
                  color: "#cd0000",
                }}
              />
            )}
          </Box>
        </DocCard>
      </Grid>
    );
  };
  return (
    <Box>
      <Grid container spacing={3}>
        {Object.entries(config).map(([docType, cfg]) =>
          renderCard(docType, cfg)
        )}
      </Grid>
      <Popover
        open={openInfo}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        disableRestoreFocus
        sx={{
          "& .MuiPopover-paper": {
            p: 1.5,
            borderRadius: 2,
            maxWidth: 260,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t(currentInfo)}
        </Typography>
      </Popover>
      <Modal
        title={t("screening.profile.redact_document")}
        open={redactionModalOpen}
        setOpen={setRedactionModalOpen}
        size="md"
        actions={false}
      >
        {previewUrl && (
          <ImageRedactor
            src={previewUrl}
            documentType={selectedDocType}
            onSave={handleRedactedSave}
          />
        )}
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity as any}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};
export default DocumentUploadSection;
